export type EvidenceMediaKind = "image" | "video";

const IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "webp",
  "gif",
  "heic",
  "heif",
  "bmp",
  "avif",
]);

/**
 * Foto ou vídeo, decidido pela extensão do arquivo na URL.
 *
 * A evidência da coleta pode ser das duas coisas — o transportador envia o que
 * o app dele registrou. O gateway devolve só a URL assinada, sem content-type,
 * então a extensão é o único sinal disponível antes de baixar o arquivo.
 *
 * Desconhecido cai em "video", que é o que a tela do Histórico sempre assumiu:
 * na dúvida, o comportamento não regride para o que já funcionava. Um `<video>`
 * apontando para imagem falha visível (player vazio); um `<img>` apontando para
 * vídeo falha igual — não há escolha segura, só a menos surpreendente.
 *
 * A URL vem assinada, com query string (`?X-Amz-...`), e pode ter fragmento —
 * os dois são descartados antes de olhar a extensão.
 */
export const getEvidenceMediaKind = (url: string): EvidenceMediaKind => {
  const semQuery = url.split("?")[0].split("#")[0];
  const ultimoSegmento = semQuery.split("/").pop() ?? "";
  const ponto = ultimoSegmento.lastIndexOf(".");
  if (ponto === -1) return "video";
  const extensao = ultimoSegmento.slice(ponto + 1).toLowerCase();
  return IMAGE_EXTENSIONS.has(extensao) ? "image" : "video";
};

export type EvidenceSide = "before" | "after";

const SIDE_LABEL: Record<EvidenceSide, string> = {
  before: "antes",
  after: "depois",
};

/**
 * Nome do arquivo salvo no download: `evidencia-antes-<coleta>.mp4`.
 *
 * A extensão sai da própria URL (sem query/fragmento). Sem extensão, cai em
 * `mp4` — o mesmo palpite de `getEvidenceMediaKind`, que trata desconhecido
 * como vídeo.
 */
export const getEvidenceDownloadFilename = (
  url: string,
  collectId: string,
  side: EvidenceSide,
): string => {
  const withoutQuery = url.split("?")[0].split("#")[0];
  const lastSegment = withoutQuery.split("/").pop() ?? "";
  const dot = lastSegment.lastIndexOf(".");
  const extension =
    dot === -1 ? "mp4" : lastSegment.slice(dot + 1).toLowerCase() || "mp4";
  return `evidencia-${SIDE_LABEL[side]}-${collectId}.${extension}`;
};
